import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { chatApi } from '../../services/chatApi';
import type { TeamCandidate, TeamPosition, TeamStrength } from '../../types/team';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  candidates?: CandidateMatch[]; // Candidates associated with this message
  candidateContext?: {
    position_id?: number;
    position_role?: string;
    count_needed?: number;
  }; // Context for action buttons
  suggestedPositions?: PositionSuggestion[]; // Positions suggested by AI for user to add
}

interface AIMatch {
  id: number;
  person: any;
  match_score: number;
  ai_reasoning: string;
  strengths: string[];
  /** Batch LLM one-liner for this candidate vs current search (when rerank ran). */
  semantic_insight?: string;
}

interface PositionSuggestion {
  id: number;
  role: string;
  count_needed: number;
  skills_required: string[];
  priority: 'critical' | 'important' | 'nice-to-have';
  reasoning?: string;
  status: 'proposed' | 'confirmed';
  source: 'ai' | 'user';
}

interface CandidateMatch {
  person_id: number;
  position_index: number;
  /** When set (from server), target slot for +Add — avoids index/order bugs */
  position_id?: number;
  match_score: number;
  scores?: {
    skillMatch: number;
    semanticMatch: number;
    teamFitBonus: number;
  };
  match_reasoning: string;
  profile: {
    name: string;
    title: string;
    team: string;
    expertise: string;
  };
}


interface TeamEvent {
  id: number;
  name: string;
  description?: string;
  isNew: boolean;
}

// Composer context passed from App.tsx for chat awareness
interface ComposerContext {
  team: {
    id: number;
    name: string;
    description?: string;
    status?: string;
  } | null;
  positions: TeamPosition[];
  candidates: TeamCandidate[];
}

interface ChatWindowProps {
  /** Search mode only: true while the server is still producing people matches (after send until `search_results` / `complete` / error). */
  onSearchPanelBusyChange?: (busy: boolean) => void;
  onResults?: (
    matches: AIMatch[],
    criteria?: { searchQuery: string; extracted: any; brief?: unknown | null }
  ) => void;
  onPositionsSuggested?: (positions: PositionSuggestion[], teamId?: number) => void;
  onCandidatesMatched?: (candidates: CandidateMatch[], teamId?: number) => void;
  onTeamStrengthUpdate?: (strength: TeamStrength, teamId?: number) => void;
  onTeamCreated?: (team: TeamEvent) => void;
  onTeamSelected?: (team: TeamEvent) => void;
  composerContext?: ComposerContext; // Current state of Team Composer
  onAddCandidateToTeam?: (personId: number, positionId?: number) => void; // Callback to add candidate to team
  onAddPositionToTeam?: (position: PositionSuggestion) => void; // Callback to add a suggested position
  onPositionHighlight?: (positionId: number | null) => void; // Callback to highlight a position
  mode?: ChatMode;
  onModeChange?: (mode: ChatMode) => void;
}

export type ChatMode = 'search' | 'team' | 'position' | 'candidates';

export interface ChatWindowHandle {
  focusInput: () => void;
}

export const ChatWindow = forwardRef<ChatWindowHandle, ChatWindowProps>(({
  onSearchPanelBusyChange,
  onResults,
  onPositionsSuggested,
  onCandidatesMatched,
  onTeamStrengthUpdate,
  onTeamCreated,
  onTeamSelected,
  composerContext,
  onAddCandidateToTeam,
  onAddPositionToTeam,
  onPositionHighlight,
  mode = 'search',
  onModeChange
}: ChatWindowProps, ref) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  /**
   * UX "waiting for reply tokens" — cleared when the server emits assistant_body_done.
   * Background work (search, auto-teams) may still run until `complete`; see pipelineFinishing.
   */
  const [loading, setLoading] = useState(false);
  /** Server-reported phase (orchestration, composing, finalizing); cleared when chunks arrive */
  const [streamPhase, setStreamPhase] = useState<string | null>(null);
  /** True after assistant_body_done until complete/error/abort — keeps Send off and shows composer banner */
  const [pipelineFinishing, setPipelineFinishing] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [initializing, setInitializing] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Track which candidates have been added via chat buttons (persists across re-renders)
  const [chatAddedCandidates, setChatAddedCandidates] = useState<Set<number>>(new Set());
  // Track which positions have been added via chat suggestion buttons
  const [chatAddedPositions, setChatAddedPositions] = useState<Set<string>>(new Set());
  const inputRef = useRef<HTMLTextAreaElement>(null);
  /** Bumps on each send so aborted pumps do not clear state for a newer request. */
  const chatRequestGenRef = useRef(0);
  const chatAbortRef = useRef<AbortController | null>(null);
  const hasTeamContext = !!composerContext?.team;
  const hasPositionContext = (composerContext?.positions?.length || 0) > 0;

  const setSearchPanelBusy = (busy: boolean) => {
    if (mode !== 'search') return;
    onSearchPanelBusyChange?.(busy);
  };

  useImperativeHandle(ref, () => ({
    focusInput: () => {
      inputRef.current?.focus();
    },
  }));

  const getInputPlaceholder = () => {
    if (hasMessages) return '';
    if (mode === 'search') return 'Describe who you are looking for...';
    if (mode === 'team') return 'Describe the team you need to build...';
    if (mode === 'position') return hasTeamContext ? 'Describe the position to add...' : 'Select or create a team first';
    return hasPositionContext ? 'Find candidates for a position...' : 'Add a position first, then find candidates';
  };

  // Initialize chat on mount
  useEffect(() => {
    initializeChat();
  }, []);

  // Auto-scroll to bottom (include streamPhase so status line changes still scroll)
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamPhase, loading, pipelineFinishing]);

  const initializeChat = async () => {
    try {
      const response = await chatApi.init();
      setSessionId(response.sessionId);
      // Don't show init message - just empty chat, ready for user input
      setMessages([]);
    } catch (error) {
      console.error('Failed to initialize chat:', error);
      setMessages([{
        role: 'assistant',
        content: 'Error: AI chat unavailable. Use search instead.',
        timestamp: new Date()
      }]);
    } finally {
      setInitializing(false);
    }
  };

  // Parse SSE events from a chunk of text
  const parseSSEEvents = (text: string): Array<{ event: string; data: any }> => {
    const events: Array<{ event: string; data: any }> = [];
    const lines = text.split('\n');
    let currentEvent = '';
    let currentData = '';

    for (const line of lines) {
      if (line.startsWith('event: ')) {
        currentEvent = line.slice(7).trim();
      } else if (line.startsWith('data: ')) {
        currentData = line.slice(6);
        if (currentEvent && currentData) {
          try {
            events.push({ event: currentEvent, data: JSON.parse(currentData) });
          } catch (e) {
            console.warn('Failed to parse SSE data:', currentData);
          }
        }
        currentEvent = '';
        currentData = '';
      }
    }
    return events;
  };

  // Build context string from composer state
  const buildComposerContextString = (): string => {
    // Pure people search: omit team block. If a team is open, keep the model aligned with the composer.
    if (mode === 'search' && !composerContext?.team) return '';
    if (!composerContext) return '';
    
    const parts: string[] = [];
    
    if (composerContext.team) {
      const t = composerContext.team as any;
      parts.push(`Current team: "${t.name}" (ID: ${t.id})`);
      
      // Include rich team metadata so AI knows the team context
      if (t.expected_deliverables) parts.push(`Team brief/deliverables: ${t.expected_deliverables}`);
      if (t.description) parts.push(`Description: ${t.description}`);
      if (t.team_type) parts.push(`Team type: ${t.team_type}`);
      if (t.location) parts.push(`Location: ${t.location}`);
      if (t.location_model) parts.push(`Work model: ${t.location_model}`);
      if (t.start_date) parts.push(`Start date: ${t.start_date}`);
      if (t.end_date) parts.push(`End date: ${t.end_date}`);
      if (t.success_criteria) parts.push(`Success criteria: ${t.success_criteria}`);
    }
    
    if (composerContext.positions.length > 0) {
      const positionsList = composerContext.positions.map(p => {
        const cands = p.candidates || [];
        const selected = cands.filter(c => c.status === 'selected');
        const proposed = cands.filter(c => c.status === 'proposed');
        const need = p.count_needed ?? 1;
        const filled = selected.length;
        const assigneePart =
          selected.length > 0
            ? ` — selected assignee(s): ${selected.map(c => c.profile?.name || `person_id ${c.person_id}`).join(', ')}`
            : ' — no selected assignee (slot empty)';
        const previewPart =
          proposed.length > 0
            ? `; preview only (not assigned): ${proposed.map(c => c.profile?.name || String(c.person_id)).join(', ')}`
            : '';
        return `- ${p.role} (position id ${p.id}, ${p.status}, needs ${need}, selected ${filled}/${need}${assigneePart}${previewPart})`;
      }).join('\n');
      parts.push(`Positions currently defined:\n${positionsList}`);

      const selectedTotal = composerContext.positions.reduce(
        (n, p) => n + (p.candidates || []).filter(c => c.status === 'selected').length,
        0
      );
      const previewTotal = composerContext.positions.reduce(
        (n, p) => n + (p.candidates || []).filter(c => c.status === 'proposed').length,
        0
      );
      if (selectedTotal > 0) {
        parts.push(
          `${selectedTotal} selected assignment(s) across positions${previewTotal > 0 ? `; ${previewTotal} in preview/proposed` : ''}.`
        );
      } else {
        parts.push('No selected assignees yet (slots empty). Preview matches may still appear in the UI.');
      }
    } else {
      parts.push('IMPORTANT: No positions/roles have been defined for this team yet. Help the user define positions by asking what roles they need. Do NOT claim positions already exist. Do NOT say the team is "ready to go" or "confirmed" without actual positions.');
    }
    
    // Always add behavioral constraints
    parts.push('\nRULES FOR THIS CONVERSATION:');
    parts.push('- A team already exists (see above). Do NOT create a new team.');
    parts.push('- Only reference positions that are explicitly listed above as currently defined. Do NOT invent or fabricate positions that don\'t exist.');
    parts.push('- Do NOT say the team is "confirmed", "ready to go", or "set up" unless positions AND candidates are actually listed above.');
    parts.push('- When you suggest roles/positions, the system will render clickable "+Add" buttons below your message once those roles are saved by the team pipeline (same reply). Do not say "click below" unless those chips are expected in this turn.');
    parts.push('- Simply describe what roles you recommend. Do NOT claim you have created or added positions -- the user must click the buttons to add them.');
    
    return parts.join('\n');
  };

  const pumpChatResponse = async (response: Response, requestGen: number) => {
    const contentType = response.headers.get('content-type') || '';
    const acc = { content: '' };

    const dispatchSseEvents = (events: Array<{ event: string; data: any }>) => {
      for (const { event, data } of events) {
        if (requestGen !== chatRequestGenRef.current) return;
        // Chat tab can stay "Search" after ✨ AI (locked) while a team is loaded — still apply team SSE to composer.
        const applyTeamComposerSse = hasTeamContext || mode !== 'search';
        switch (event) {
          case 'assistant_body_done':
            setLoading(false);
            setPipelineFinishing(true);
            break;

          case 'stream_status': {
            const label = typeof data?.label === 'string' ? data.label : null;
            const phase = typeof data?.phase === 'string' ? data.phase : '';
            setStreamPhase(
              label ||
                (phase === 'working'
                  ? 'Working on your request…'
                  : phase === 'composing'
                    ? 'Generating reply…'
                    : phase === 'team_build'
                      ? 'Updating teams and positions…'
                      : phase === 'finalizing'
                        ? 'Applying background updates…'
                        : phase || null)
            );
            break;
          }

          case 'chat_chunk':
            acc.content += data.chunk || '';
            setStreamPhase(null);
            setMessages(prev => {
              const updated = [...prev];
              const lastIndex = updated.length - 1;
              if (lastIndex >= 0 && updated[lastIndex].role === 'assistant') {
                updated[lastIndex] = {
                  ...updated[lastIndex],
                  content: acc.content
                };
              }
              return updated;
            });
            break;

          case 'search_results':
            setSearchPanelBusy(false);
            if (Array.isArray(data.matches) && onResults) {
              onResults(data.matches, {
                searchQuery: data.searchQuery,
                extracted: data.extracted,
                brief: data.brief ?? null,
              });
            }
            break;

          case 'complete':
            setPipelineFinishing(false);
            setStreamPhase(null);
            setSearchPanelBusy(false);
            if (data.message) {
              setMessages(prev => {
                const updated = [...prev];
                const lastIndex = updated.length - 1;
                if (lastIndex >= 0 && updated[lastIndex].role === 'assistant') {
                  const prevMsg = updated[lastIndex];
                  const fromComplete =
                    Array.isArray(data.suggested_positions) && data.suggested_positions.length > 0
                      ? data.suggested_positions
                      : undefined;
                  const suggestedPositions =
                    fromComplete ?? prevMsg.suggestedPositions;
                  updated[lastIndex] = {
                    ...prevMsg,
                    content: data.message,
                    ...(suggestedPositions && suggestedPositions.length > 0
                      ? { suggestedPositions }
                      : {}),
                  };
                }
                return updated;
              });
            }
            if (Array.isArray(data.matches) && onResults) {
              onResults(data.matches, {
                searchQuery: data.searchQuery,
                extracted: data.extracted,
                brief: data.brief ?? null,
              });
            }
            // Echo from `complete` (backend) so composer gets positions even if `positions_suggested` was missed.
            if (
              applyTeamComposerSse &&
              onPositionsSuggested &&
              Array.isArray(data.suggested_positions) &&
              data.suggested_positions.length > 0
            ) {
              const first = data.suggested_positions[0] as { team_id?: number };
              const tid = typeof first?.team_id === 'number' ? first.team_id : undefined;
              onPositionsSuggested(data.suggested_positions, tid);
            }
            break;

          case 'chat_error':
          case 'search_error':
          case 'error':
            console.error('SSE error:', data.error);
            setPipelineFinishing(false);
            setStreamPhase(null);
            setSearchPanelBusy(false);
            setMessages(prev => {
              const updated = [...prev];
              const lastIndex = updated.length - 1;
              if (lastIndex >= 0 && updated[lastIndex].role === 'assistant') {
                updated[lastIndex] = {
                  ...updated[lastIndex],
                  content: acc.content || `Error: ${data.error}`
                };
              }
              return updated;
            });
            break;

          case 'team_created':
            if (!applyTeamComposerSse) break;
            console.log('✨ Received team_created:', data);
            if (onTeamCreated) {
              onTeamCreated(data);
            }
            break;

          case 'team_selected':
            if (!applyTeamComposerSse) break;
            console.log('📌 Received team_selected:', data);
            if (onTeamSelected) {
              onTeamSelected(data);
            }
            break;

          case 'positions_suggested':
            console.log('📋 Received positions_suggested:', data.positions);
            // Always attach +Add chips to the assistant bubble (even if chat mode is still "search").
            if (data.positions) {
              setMessages(prev => {
                const updated = [...prev];
                const lastIndex = updated.length - 1;
                if (lastIndex >= 0 && updated[lastIndex].role === 'assistant') {
                  updated[lastIndex] = {
                    ...updated[lastIndex],
                    suggestedPositions: data.positions
                  };
                }
                return updated;
              });
            }
            if (applyTeamComposerSse && data.positions && onPositionsSuggested) {
              onPositionsSuggested(data.positions, data.team_id);
            }
            break;

          case 'candidates_matched':
            console.log('👥 Received candidates_matched:', data);
            setMessages(prev => {
              const updated = [...prev];
              const lastIndex = updated.length - 1;
              if (lastIndex >= 0 && updated[lastIndex].role === 'assistant') {
                updated[lastIndex] = {
                  ...updated[lastIndex],
                  candidates: data.candidates || [],
                  candidateContext: {
                    position_id: data.position_id,
                    position_role: data.position_role,
                    count_needed: data.count_needed
                  }
                };
              }
              return updated;
            });
            if (applyTeamComposerSse && data.candidates && onCandidatesMatched) {
              onCandidatesMatched(data.candidates, data.team_id);
            }
            break;

          case 'team_strength_update':
            if (!applyTeamComposerSse) break;
            console.log('💪 Received team_strength_update:', data);
            if (onTeamStrengthUpdate) {
              onTeamStrengthUpdate(data, data.team_id);
            }
            break;

          case 'position_search_started':
            if (!applyTeamComposerSse) break;
            console.log('🔍 Position search started:', data);
            if (data.position_id && onPositionHighlight) {
              onPositionHighlight(data.position_id);
            }
            break;

          case 'candidate_found':
            if (!applyTeamComposerSse) break;
            console.log('👤 Received candidate_found:', data);
            if (data.person_id && onAddCandidateToTeam) {
              let targetPositionId: number | undefined;
              if (data.target_position_role && composerContext?.positions) {
                const matchingPosition = composerContext.positions.find(
                  p =>
                    p.role.toLowerCase().includes(data.target_position_role.toLowerCase()) ||
                    data.target_position_role.toLowerCase().includes(p.role.toLowerCase())
                );
                if (matchingPosition) {
                  targetPositionId = matchingPosition.id;
                  console.log(`👤 Found matching position: ${matchingPosition.role} (ID: ${targetPositionId})`);
                }
              }
              onAddCandidateToTeam(data.person_id, targetPositionId);
              console.log(`✅ Added ${data.person?.name} to position ${targetPositionId || 'first available'}`);
            }
            break;

          case 'team_composer_error':
            console.error('Team composer error:', data.error);
            break;

          default:
            break;
        }
      }
    };

    if (contentType.includes('text/event-stream')) {
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      if (!reader) {
        throw new Error('No response body');
      }

      while (true) {
        if (requestGen !== chatRequestGenRef.current) {
          try {
            await reader.cancel();
          } catch {
            /* ignore */
          }
          return;
        }
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        const eventEndIndex = buffer.lastIndexOf('\n\n');
        if (eventEndIndex !== -1) {
          const completeEvents = buffer.substring(0, eventEndIndex + 2);
          buffer = buffer.substring(eventEndIndex + 2);
          dispatchSseEvents(parseSSEEvents(completeEvents));
        }
      }

      buffer += decoder.decode();
      if (buffer.trim()) {
        dispatchSseEvents(parseSSEEvents(buffer));
      }
      return;
    }

    const data = await response.json();
    if (requestGen !== chatRequestGenRef.current) return;

    if (data.type === 'chat' || data.type === 'clarify') {
      setMessages(prev => {
        const updated = [...prev];
        const lastIndex = updated.length - 1;
        if (lastIndex >= 0 && updated[lastIndex].role === 'assistant') {
          updated[lastIndex] = {
            ...updated[lastIndex],
            content: data.chat_message || 'How can I help?'
          };
        }
        return updated;
      });
    } else if (data.type === 'results') {
      setSearchPanelBusy(false);
      setMessages(prev => {
        const updated = [...prev];
        const lastIndex = updated.length - 1;
        if (lastIndex >= 0 && updated[lastIndex].role === 'assistant') {
          updated[lastIndex] = {
            ...updated[lastIndex],
            content: data.chat_message || 'Found matches!'
          };
        }
        return updated;
      });
      if (data.matches && onResults) {
        onResults(data.matches, {
          searchQuery: data.searchQuery,
          extracted: data.extracted,
          brief: data.brief ?? null,
        });
      }
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || !sessionId) return;

    const myGen = ++chatRequestGenRef.current;
    chatAbortRef.current?.abort();
    const ac = new AbortController();
    chatAbortRef.current = ac;

    const userMessage: Message = {
      role: 'user',
      content: input,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const userInput = input;
    setInput('');
    setLoading(true);
    setStreamPhase(null);
    setPipelineFinishing(false);
    setSearchPanelBusy(true);

    const aiMessage: Message = {
      role: 'assistant',
      content: '',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, aiMessage]);

    const contextString = buildComposerContextString();
    const messageWithContext = contextString
      ? `[CURRENT TEAM CONTEXT]\n${contextString}\n\n[USER MESSAGE]\n${userInput}`
      : userInput;

    try {
      const response = await chatApi.sendMessage(messageWithContext, sessionId, mode, ac.signal);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      await pumpChatResponse(response, myGen);
      if (chatRequestGenRef.current === myGen) {
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    } catch (error: any) {
      if (chatRequestGenRef.current !== myGen) return;
      if (error?.name === 'AbortError') {
        setLoading(false);
        setStreamPhase(null);
        setPipelineFinishing(false);
        setSearchPanelBusy(false);
        return;
      }
      setSearchPanelBusy(false);
      setMessages(prev => {
        const updated = [...prev];
        const lastIndex = updated.length - 1;
        if (lastIndex >= 0 && updated[lastIndex].role === 'assistant') {
          updated[lastIndex] = {
            ...updated[lastIndex],
            content: `Error: ${error.message || 'Failed to get response'}`
          };
        }
        return updated;
      });
      setLoading(false);
      setStreamPhase(null);
      setPipelineFinishing(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    } finally {
      if (chatRequestGenRef.current === myGen) {
        setLoading(false);
        setStreamPhase(null);
        setPipelineFinishing(false);
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Handle clicking on bold text options in AI responses
  const handleOptionClick = (text: string) => {
    if (!sessionId) return;
    
    // Set input and immediately send
    setInput(text);
    
    // Use setTimeout to ensure state is updated before sending
    setTimeout(() => {
      const userMessage: Message = {
        role: 'user',
        content: text,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, userMessage]);
      setInput('');
      
      // Trigger the actual send
      sendMessageWithText(text);
    }, 0);
  };

  // Send a specific message text (used by option clicks)
  const sendMessageWithText = async (messageText: string) => {
    if (!messageText.trim() || !sessionId) return;

    const myGen = ++chatRequestGenRef.current;
    chatAbortRef.current?.abort();
    const ac = new AbortController();
    chatAbortRef.current = ac;

    setLoading(true);
    setStreamPhase(null);
    setPipelineFinishing(false);
    setSearchPanelBusy(true);

    const aiMessage: Message = {
      role: 'assistant',
      content: '',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, aiMessage]);

    const contextString = buildComposerContextString();
    const messageWithContext = contextString
      ? `[CURRENT TEAM CONTEXT]\n${contextString}\n\n[USER MESSAGE]\n${messageText}`
      : messageText;

    try {
      const response = await chatApi.sendMessage(messageWithContext, sessionId, mode, ac.signal);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      await pumpChatResponse(response, myGen);
      if (chatRequestGenRef.current === myGen) {
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    } catch (error: any) {
      if (chatRequestGenRef.current !== myGen) return;
      if (error?.name === 'AbortError') {
        setLoading(false);
        setStreamPhase(null);
        setPipelineFinishing(false);
        setSearchPanelBusy(false);
        return;
      }
      setSearchPanelBusy(false);
      setMessages(prev => {
        const updated = [...prev];
        const lastIndex = updated.length - 1;
        if (lastIndex >= 0 && updated[lastIndex].role === 'assistant') {
          updated[lastIndex] = {
            ...updated[lastIndex],
            content: `Error: ${error.message || 'Failed to get response'}`
          };
        }
        return updated;
      });
      setLoading(false);
      setStreamPhase(null);
      setPipelineFinishing(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    } finally {
      if (chatRequestGenRef.current === myGen) {
        setLoading(false);
        setStreamPhase(null);
        setPipelineFinishing(false);
      }
    }
  };

  // Group candidates by position for rendering
  const groupCandidatesByPosition = (candidates: CandidateMatch[]) => {
    const groups: Record<string, { position: PositionSuggestion | undefined; candidates: CandidateMatch[] }> = {};

    // Keep composer array order — must match backend position_index (orchestration order).
    // Do NOT sort by id: that breaks index→slot mapping and puts every +Add on one role.
    const positions = composerContext?.positions || [];
    const orderedPositions = [...positions];

    console.log('📊 Grouping candidates by position:', {
      candidateCount: candidates.length,
      positionsCount: orderedPositions.length,
      positions: orderedPositions.map(p => ({ id: p.id, role: p.role })),
    });

    candidates.forEach(candidate => {
      let position: PositionSuggestion | undefined;
      if (candidate.position_id != null && candidate.position_id !== 0) {
        position = orderedPositions.find(p => p.id === candidate.position_id);
      }
      if (!position) {
        const posIdx = candidate.position_index ?? 0;
        position = orderedPositions[posIdx];
      }
      const gkey = String(position?.id ?? `idx:${candidate.position_index ?? 0}`);
      if (!groups[gkey]) {
        groups[gkey] = { position, candidates: [] };
        console.log(`🔗 Group ${gkey} →`, position?.role || 'unknown');
      }
      groups[gkey].candidates.push(candidate);
    });

    return groups;
  };
  
  // Compact candidate chip component (per SVG design)
  const CandidateChip = ({ 
    candidate, 
    position,
    isSelected,
    onAdd
  }: { 
    candidate: CandidateMatch; 
    position?: PositionSuggestion;
    isSelected?: boolean;
    onAdd?: () => void;
  }) => {
    // Only check isSelected from parent - don't check position.candidates
    // because AI-matched candidates in position.candidates are "proposed", not "added"
    // The user must explicitly add them via action buttons
    const isAlreadyAdded = isSelected === true;
    
    const handleAddClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      console.log('🎯 Chip [+Add] clicked:', { 
        personId: candidate.person_id, 
        personName: candidate.profile?.name,
        positionId: position?.id,
        positionRole: position?.role,
        hasCallback: !!onAddCandidateToTeam,
        hasOnAdd: !!onAdd,
        hasPosition: !!position 
      });
      
      // Use onAdd callback if provided (from PositionCandidatesBlock), else direct
      if (onAdd) {
        onAdd();
      } else if (onAddCandidateToTeam && position) {
        onAddCandidateToTeam(candidate.person_id, position.id);
      } else {
        console.warn('❌ Cannot add: missing callback or position', { 
          onAddCandidateToTeam: !!onAddCandidateToTeam, 
          position 
        });
      }
    };
    
    // Green for selected/added, yellow/amber for proposed
    const chipStyle = isAlreadyAdded
      ? 'bg-emerald-900/40 border-emerald-500 text-emerald-400'
      : 'bg-amber-900/30 border-amber-500 text-amber-400 hover:bg-amber-900/50';
    
    const statusIcon = isAlreadyAdded ? '[x]' : '[?]';
    
    return (
      <div 
        className={`inline-flex items-center gap-1 px-2 py-1 border rounded text-xs font-medium cursor-pointer transition-colors ${chipStyle}`}
        title={candidate.match_reasoning || `${candidate.profile?.title || ''}`}
      >
        <span className={isAlreadyAdded ? 'text-emerald-400' : 'text-amber-300'}>{statusIcon}</span>
        <span>{candidate.profile?.name || 'Unknown'}</span>
        <span className="opacity-70">-</span>
        <span className="font-bold">{candidate.match_score}%</span>
        {!isAlreadyAdded && (onAdd || (onAddCandidateToTeam && position)) && (
          <button
            onClick={handleAddClick}
            className="ml-1 px-1 py-0.5 bg-amber-500/30 hover:bg-amber-500/50 rounded text-amber-300 font-bold transition-colors"
            title={`Add ${candidate.profile?.name} to ${position?.role || 'position'}`}
          >
            +Add
          </button>
        )}
      </div>
    );
  };
  
  // Render candidates grouped by position with action buttons
  const PositionCandidatesBlock = ({ 
    candidates, 
    candidateContext 
  }: { 
    candidates: CandidateMatch[];
    candidateContext?: Message['candidateContext'];
  }) => {
    const groups = groupCandidatesByPosition(candidates);
    // Use parent's state to persist across re-renders
    const addedCandidates = chatAddedCandidates;
    const setAddedCandidates = setChatAddedCandidates;
    
    // Handle adding specific candidates by count
    const handleAddCandidates = (position: PositionSuggestion | undefined, candidatesToAdd: CandidateMatch[], count: number) => {
      if (!onAddCandidateToTeam || !position) return;
      
      // Sort by score and take the top N that aren't already added
      const sortedCandidates = [...candidatesToAdd]
        .sort((a, b) => b.match_score - a.match_score)
        .filter(c => !addedCandidates.has(c.person_id));
      
      const toAdd = sortedCandidates.slice(0, count);
      const newAdded = new Set(addedCandidates);
      
      toAdd.forEach(candidate => {
        console.log(`✨ Adding ${candidate.profile?.name} (${candidate.match_score}%) to ${position.role}`);
        onAddCandidateToTeam(candidate.person_id, position.id);
        newAdded.add(candidate.person_id);
      });
      
      setAddedCandidates(newAdded);
    };
    
    return (
      <div className="mt-3 space-y-3">
        {Object.entries(groups).map(([posIdx, { position, candidates: positionCandidates }]) => {
          const countNeeded = candidateContext?.count_needed || position?.count_needed || 1;
          const notAddedCandidates = positionCandidates.filter(c => !addedCandidates.has(c.person_id));
          const allAdded = notAddedCandidates.length === 0;
          
          return (
            <div 
              key={posIdx}
              className="bg-slate-800/50 border border-blue-500/30 rounded-md p-3"
            >
              {/* Position header */}
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-bold text-cyan-400">
                  {position?.role || candidateContext?.position_role || `Position (${posIdx})`} 
                  <span className="font-normal text-slate-400 ml-1">
                    ({positionCandidates.length} candidates, need {countNeeded})
                  </span>
                </div>
              </div>
              
              {/* Candidate chips */}
              <div className="flex flex-wrap gap-2 mb-3">
                {positionCandidates.map((candidate, idx) => (
                  <CandidateChip
                    key={`${candidate.person_id}-${idx}`}
                    candidate={candidate}
                    position={position}
                    isSelected={addedCandidates.has(candidate.person_id)}
                    onAdd={() => {
                      if (onAddCandidateToTeam && position) {
                        onAddCandidateToTeam(candidate.person_id, position.id);
                        setAddedCandidates(prev => new Set(prev).add(candidate.person_id));
                      }
                    }}
                  />
                ))}
              </div>
              
              {/* Action buttons - only show if there are candidates to add */}
              {!allAdded && position && onAddCandidateToTeam && (
                <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-700/50">
                  {/* Best Match button - always show */}
                  <button
                    onClick={() => handleAddCandidates(position, positionCandidates, 1)}
                    className="px-3 py-1.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white text-xs font-semibold rounded-lg transition-all shadow-md hover:shadow-lg"
                    title={`Add the highest scoring candidate to ${position.role}`}
                  >
                    ✨ Add Best Match
                  </button>
                  
                  {/* Fill Position button - only if count_needed > 1 */}
                  {countNeeded > 1 && notAddedCandidates.length >= countNeeded && (
                    <button
                      onClick={() => handleAddCandidates(position, positionCandidates, countNeeded)}
                      className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white text-xs font-semibold rounded-lg transition-all shadow-md hover:shadow-lg"
                      title={`Add the top ${countNeeded} candidates to fill ${position.role}`}
                    >
                      ✨ Fill Position ({countNeeded})
                    </button>
                  )}
                  
                  {/* Add All button - only if more candidates than needed */}
                  {notAddedCandidates.length > 1 && notAddedCandidates.length > countNeeded && (
                    <button
                      onClick={() => handleAddCandidates(position, positionCandidates, notAddedCandidates.length)}
                      className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs font-medium rounded-lg transition-all"
                      title={`Add all ${notAddedCandidates.length} candidates to ${position.role}`}
                    >
                      Add All ({notAddedCandidates.length})
                    </button>
                  )}
                </div>
              )}
              
              {/* All added confirmation */}
              {allAdded && positionCandidates.length > 0 && (
                <div className="text-xs text-emerald-400 pt-2 border-t border-slate-700/50">
                  ✓ All candidates added to position
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Render suggested positions with +Add buttons (inline in chat message)
  const PositionSuggestionsBlock = ({ positions }: { positions: PositionSuggestion[] }) => {
    const addedPositions = chatAddedPositions;

    const handleAddPosition = (position: PositionSuggestion) => {
      if (!onAddPositionToTeam) return;
      const key = position.role.toLowerCase();
      if (addedPositions.has(key)) return;
      
      onAddPositionToTeam(position);
      setChatAddedPositions(prev => new Set(prev).add(key));
    };

    const handleAddAll = () => {
      if (!onAddPositionToTeam) return;
      const newAdded = new Set(addedPositions);
      positions.forEach(p => {
        const key = p.role.toLowerCase();
        if (!newAdded.has(key)) {
          onAddPositionToTeam(p);
          newAdded.add(key);
        }
      });
      setChatAddedPositions(newAdded);
    };

    const notAdded = positions.filter(p => !addedPositions.has(p.role.toLowerCase()));
    const allAdded = notAdded.length === 0;

    return (
      <div className="mt-3 bg-slate-800/50 border border-indigo-500/30 rounded-md p-3">
        <div className="text-xs font-bold text-indigo-400 mb-2">
          Suggested Positions
        </div>
        <div className="flex flex-wrap gap-2 mb-2">
          {positions.map((pos, idx) => {
            const isAdded = addedPositions.has(pos.role.toLowerCase());
            return (
              <button
                key={`${pos.role}-${idx}`}
                onClick={() => handleAddPosition(pos)}
                disabled={isAdded || !onAddPositionToTeam}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  isAdded
                    ? 'bg-emerald-900/40 border border-emerald-500 text-emerald-400 cursor-default'
                    : 'bg-indigo-600 hover:bg-indigo-500 text-white cursor-pointer'
                }`}
              >
                {isAdded ? `v ${pos.role}` : `+ Add ${pos.role}`}
              </button>
            );
          })}
        </div>
        {!allAdded && onAddPositionToTeam && notAdded.length > 1 && (
          <button
            onClick={handleAddAll}
            className="px-3 py-1.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white text-xs font-semibold rounded-lg transition-all"
          >
            Add all {notAdded.length} positions
          </button>
        )}
        {allAdded && (
          <div className="text-xs text-emerald-400">
            All positions added to team
          </div>
        )}
      </div>
    );
  };

  // Custom renderer for links - makes [option](#) clickable options
// @ts-ignore
  const ClickableLink = ({ href, children, ..._props }: any) => {
    const text = typeof children === 'string' 
      ? children 
      : Array.isArray(children) 
        ? children.map(c => typeof c === 'string' ? c : '').join('') 
        : '';
    
    // Only make it a clickable option if href is "#" or starts with "select:"
    const isClickableOption = href === '#' || href?.startsWith('select:');
    
    if (isClickableOption) {
      return (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            handleOptionClick(text);
          }}
          className="inline-flex items-center cursor-pointer bg-brand-blue/20 hover:bg-brand-blue/40 text-brand-blue hover:text-white px-2 py-0.5 rounded-md transition-all border border-brand-blue/30 hover:border-brand-blue font-medium text-sm"
          title="Click to select this option"
        >
          {children}
        </button>
      );
    }
    
    // Regular link - open in new tab
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className="text-brand-blue hover:underline">
        {children}
      </a>
    );
  };

  // Check if chat has any messages (not a new chat)
  const hasMessages = messages.length > 0;
  /** Composer strip: post-token phases and idle-time status (avoid duplicating the in-bubble line while `loading`). */
  const showComposerAgentBanner = pipelineFinishing || (!loading && !!streamPhase);
  const agentActivityMessage = showComposerAgentBanner
    ? streamPhase || (pipelineFinishing ? 'Applying background updates…' : null)
    : null;

  // Clear chat content and backend session (fresh context)
  const handleClearChat = async () => {
    if (messages.length === 0) return;
    if (window.confirm('Clear chat history? This will start a fresh conversation.')) {
      try {
        const response = await chatApi.clearAndReinit(sessionId);
        setSessionId(response.sessionId);
        setMessages([]);
        setChatAddedCandidates(new Set());
        setChatAddedPositions(new Set());
      } catch (e) {
        console.error('Failed to clear chat:', e);
        setMessages([]);
      }
    }
  };

  return (
    <div data-testid="chat-window" className="flex flex-col h-full bg-dark-card border border-dark-border rounded-xl">
      {/* Header */}
      <div className="px-3 py-2 border-b border-dark-border bg-brand-gradient text-white rounded-t-xl">
        <div className="flex items-center justify-between gap-2">
          <div className="inline-flex rounded-lg bg-black/25 border border-white/15 p-1">
            {([
              { id: 'search', label: 'Search' },
              { id: 'team', label: 'Team' },
              { id: 'position', label: 'Position' },
              { id: 'candidates', label: 'Candidates' },
            ] as const).map(tab => {
              const disabled = (tab.id === 'position' && !hasTeamContext) || (tab.id === 'candidates' && !hasPositionContext);
              const active = mode === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => !disabled && onModeChange?.(tab.id)}
                  disabled={disabled}
                  className={`px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                    active
                      ? 'bg-white/25 text-white'
                      : disabled
                      ? 'text-white/40 cursor-not-allowed'
                      : 'text-white/75 hover:text-white hover:bg-white/10'
                  }`}
                  title={
                    disabled
                      ? tab.id === 'position'
                        ? 'Select or create a team first'
                        : 'Add at least one position first'
                      : `Switch to ${tab.label} mode`
                  }
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
          {/* Clear button - only show when there are messages */}
          {hasMessages && (
            <button
              onClick={handleClearChat}
              className="text-xs text-white/70 hover:text-white transition-colors flex items-center gap-1"
              title="Clear chat history"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {initializing ? (
          <div className="text-center text-dark-text-secondary py-8">
            <div className="text-4xl mb-2">⏳</div>
            <p>Initializing AI assistant...</p>
          </div>
        ) : (
          messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex ${msg.role === 'user' ? 'justify-end pl-2' : 'justify-start pr-2'}`}
            >
              <div
                data-testid={msg.role === 'assistant' ? 'chat-message-assistant' : undefined}
                className={`max-w-[95%] p-2.5 rounded-lg ${
                  msg.role === 'user'
                    ? 'bg-brand-blue text-white'
                    : `bg-dark-bg text-dark-text border ${
                        (loading || pipelineFinishing) && idx === messages.length - 1
                          ? 'border-brand-blue/50 shadow-[0_0_0_1px_rgba(59,130,246,0.15)]'
                          : 'border-dark-border'
                      }`
                }`}
              >
                {msg.role === 'assistant' ? (
                  <div>
                    {msg.content === '' &&
                      (loading || pipelineFinishing) &&
                      idx === messages.length - 1 && (
                      <div data-testid="chat-loading" className="text-dark-text-secondary text-sm mb-1">
                        <span className="animate-pulse">{streamPhase || 'Waiting for response…'}</span>
                      </div>
                    )}
                    {msg.content !== '' && (
                    <div className="prose prose-sm prose-invert max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-headings:my-2 prose-code:text-brand-blue prose-code:bg-dark-card prose-code:px-1 prose-code:rounded">
                      <ReactMarkdown
                        components={{
                          a: ClickableLink
                        }}
                      >
                        {msg.content}
                      </ReactMarkdown>
                      {(loading || pipelineFinishing) && idx === messages.length - 1 && (
                        <span
                          className="inline-block w-0.5 h-4 ml-0.5 bg-brand-blue animate-pulse rounded-sm align-middle translate-y-0.5"
                          aria-hidden
                        />
                      )}
                    </div>
                    )}
                    {/* Render suggested positions with +Add buttons */}
                    {msg.suggestedPositions && msg.suggestedPositions.length > 0 && (
                      <PositionSuggestionsBlock positions={msg.suggestedPositions} />
                    )}
                    {/* Render inline candidates grouped by position with action buttons */}
                    {msg.candidates && msg.candidates.length > 0 && (
                      <PositionCandidatesBlock 
                        candidates={msg.candidates} 
                        candidateContext={msg.candidateContext}
                      />
                    )}
                  </div>
                ) : (
                  <div className="whitespace-pre-wrap">{msg.content}</div>
                )}
                <div className="text-xs opacity-70 mt-1">
                  {msg.timestamp.toLocaleTimeString()}
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-3 border-t border-dark-border">
        {agentActivityMessage && (
          <div
            className="mb-2 flex items-center gap-2 rounded-lg border border-brand-blue/35 bg-brand-blue/10 px-2.5 py-2 text-xs text-brand-blue"
            role="status"
            aria-live="polite"
            data-testid="chat-agent-activity"
          >
            <svg
              className="h-4 w-4 shrink-0 animate-spin text-brand-blue"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path
                className="opacity-90"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span className="leading-snug text-dark-text">{agentActivityMessage}</span>
          </div>
        )}
        <div className="flex gap-2">
          <textarea
            ref={inputRef}
            autoFocus
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={getInputPlaceholder()}
            className="flex-1 px-3 py-2 bg-dark-bg border border-dark-border rounded-lg text-dark-text placeholder-dark-text-secondary resize-none text-sm"
            rows={2}
            disabled={!sessionId}
          />
          <button
            onClick={sendMessage}
            disabled={loading || pipelineFinishing || !input.trim() || !sessionId}
            className="px-4 bg-brand-gradient text-white font-semibold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 text-sm"
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
});

